import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { DiscountApprovalProvider } from './contexts/DiscountApprovalContext';
import { hasAnalyticsAccess, hasReportsAccess, canManageReports } from './utils/accessControl';
import { useBusinessInfo } from './hooks/useBusinessInfo';
import { useBranchFeatures } from './hooks/useBranchFeatures';
import { useAppStore } from './stores/useAppStore';
import Layout from './components/Layout';
import Login from './components/Login';
import POSInterface from './components/POS/POSInterface';
import ItemManagement from './components/Items/ItemManagement';
import ItemStockHistory from './components/Items/ItemStockHistory';
import SalesList from './components/Sales/SalesList';
import SaleDetail from './components/Sales/SaleDetail';
import SaleReturnList from './components/SaleReturn/SaleReturnList';
import SaleReturnDetail from './components/SaleReturn/SaleReturnDetail';
import SaleReturnEdit from './components/SaleReturn/SaleReturnEdit';
import SaleReturnForm from './components/SaleReturn/SaleReturnForm';
import PurchaseList from './components/Purchases/PurchaseList';
import PurchaseDetail from './components/Purchases/PurchaseDetail';
import PurchaseForm from './components/Purchases/PurchaseForm';
import PurchaseReturnList from './components/PurchaseReturn/PurchaseReturnList';
import PurchaseReturnDetail from './components/PurchaseReturn/PurchaseReturnDetail';
import PurchaseReturnForm from './components/PurchaseReturn/PurchaseReturnForm';
import DamagesPage from './components/Damages/DamagesPage';
import DamageDispatchList from './components/Damages/DamageDispatchList';
import DamageDispatchDetail from './components/Damages/DamageDispatchDetail';
import ExchangeList from './components/Exchange/ExchangeList';
import ExchangeDetail from './components/Exchange/ExchangeDetail';
import ExchangeForm from './components/Exchange/ExchangeForm';
import { ClosingList, ClosingForm, ClosingDetail } from './components/Closing';
import { ExpenseList } from './components/Expenses';
import AnalyticsDashboard from './components/Analytics/AnalyticsDashboard';
import Analytics2Dashboard from './components/Analytics2/Analytics2Dashboard';
import { BusinessInformationPage } from './components/BusinessInformation';
import SalesmanScanner from './components/Salesman/SalesmanScanner';
import ReportList from './components/Reports/ReportList';
import ReportBuilder from './components/Reports/ReportBuilder';
import ReportRunner from './components/Reports/ReportRunner';
import ReportEditDispatcher from './components/Reports/ReportEditDispatcher';
import SqlWorkbench from './components/Reports/SqlWorkbench';
import NotificationContainer from './components/layout/NotificationContainer';
import UpdateNotifier from './components/UpdateNotifier';
import DynamicFavicon from './components/DynamicFavicon';


const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// A page that only exists on branches that switched its feature on (branches.json `features`).
// Waits for the flags to load rather than treating "still loading" as "off".
const FeatureRoute = ({ feature, children }) => {
  const features = useAppStore((s) => s.features);

  if (features === null) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!features[feature]) {
    return <div className="p-8 text-center text-gray-600">This page is not enabled for this branch.</div>;
  }

  return children;
};

const AnalyticsProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user has access to analytics
  if (!hasAnalyticsAccess(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">You don't have permission to access the Analytics page.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

const AdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user is admin (user ID 1)
  if (user?.id !== 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">Only admin users can access Business Information settings.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

const ManagerProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user is admin (user ID 1) or manager (user ID 2) or has admin privileges
  if (user?.id !== 1 && user?.id !== 2 && !user?.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">Only admin and manager users can access Closing operations.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};

const SalesmanProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user is salesman (user ID 4)
  if (user?.id !== 4) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">Only salesmen can access the scanner page.</p>
          <button
            onClick={() => window.history.back()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  return children;
};


// Reports: managers and admins may view/run; only admins may build or edit.
const AccessDenied = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
      <p className="text-gray-600 mb-6">{message}</p>
      <button
        onClick={() => window.history.back()}
        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        Go Back
      </button>
    </div>
  </div>
);

const ReportsProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!hasReportsAccess(user)) return <AccessDenied message="Only admin and manager users can access Reports." />;
  return children;
};

const ReportsAdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!canManageReports(user)) return <AccessDenied message="Only admin users can create or edit reports." />;
  return children;
};


const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/" /> : <Login />} 
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <POSInterface />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            <Layout>
              <SalesmanScanner />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/items"
        element={
          <ProtectedRoute>
            <Layout>
              <ItemManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/items/:id/history"
        element={
          <ProtectedRoute>
            <Layout>
              <ItemStockHistory />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            <Layout>
              <SalesList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SaleDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sale-return"
        element={
          <ProtectedRoute>
            <Layout>
              <SaleReturnList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sale-return/new"
        element={
          <ProtectedRoute>
            <Layout>
              <SaleReturnForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sale-return/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SaleReturnDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases/new"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchases/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/damages"
        element={
          <ProtectedRoute>
            <Layout>
              <FeatureRoute feature="damages">
                <DamagesPage />
              </FeatureRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/damages/dispatches"
        element={
          <ProtectedRoute>
            <Layout>
              <FeatureRoute feature="damages">
                <DamageDispatchList />
              </FeatureRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/damages/dispatches/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <FeatureRoute feature="damages">
                <DamageDispatchDetail />
              </FeatureRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-returns"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseReturnList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-returns/new"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseReturnForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/purchase-returns/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <PurchaseReturnDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange"
        element={
          <ProtectedRoute>
            <Layout>
              <ExchangeList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange/new"
        element={
          <ProtectedRoute>
            <Layout>
              <ExchangeForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ExchangeDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/exchange/:id/edit"
        element={
          <ProtectedRoute>
            <Layout>
              <ExchangeForm />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
            <Layout>
              <ExpenseList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/closings"
        element={
          <ManagerProtectedRoute>
            <Layout>
              <ClosingList />
            </Layout>
          </ManagerProtectedRoute>
        }
      />
      <Route
        path="/closings/new"
        element={
          <ManagerProtectedRoute>
            <Layout>
              <ClosingForm />
            </Layout>
          </ManagerProtectedRoute>
        }
      />
      <Route
        path="/closings/:id"
        element={
          <ManagerProtectedRoute>
            <Layout>
              <ClosingDetail />
            </Layout>
          </ManagerProtectedRoute>
        }
      />
      {/* Edit route commented out - editing disabled */}
      {/* <Route
        path="/closings/:id/edit"
        element={
          <ManagerProtectedRoute>
            <Layout>
              <ClosingForm />
            </Layout>
          </ManagerProtectedRoute>
        }
      /> */}
      <Route
        path="/analytics"
        element={
          <AnalyticsProtectedRoute>
            <Layout>
              <AnalyticsDashboard />
            </Layout>
          </AnalyticsProtectedRoute>
        }
      />
      <Route
        path="/analytics2"
        element={
          <AnalyticsProtectedRoute>
            <Layout>
              <Analytics2Dashboard />
            </Layout>
          </AnalyticsProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ReportsProtectedRoute>
            <Layout>
              <ReportList />
            </Layout>
          </ReportsProtectedRoute>
        }
      />
      <Route
        path="/reports/new"
        element={
          <ReportsAdminProtectedRoute>
            <Layout>
              <ReportBuilder />
            </Layout>
          </ReportsAdminProtectedRoute>
        }
      />
      <Route
        path="/reports/sql"
        element={
          <ReportsAdminProtectedRoute>
            <Layout>
              <SqlWorkbench />
            </Layout>
          </ReportsAdminProtectedRoute>
        }
      />
      <Route
        path="/reports/:id"
        element={
          <ReportsProtectedRoute>
            <Layout>
              <ReportRunner />
            </Layout>
          </ReportsProtectedRoute>
        }
      />
      <Route
        path="/reports/:id/edit"
        element={
          <ReportsAdminProtectedRoute>
            <Layout>
              <ReportEditDispatcher />
            </Layout>
          </ReportsAdminProtectedRoute>
        }
      />
      <Route
        path="/business-information"
        element={
          <AdminProtectedRoute>
            <Layout>
              <BusinessInformationPage />
            </Layout>
          </AdminProtectedRoute>
        }
      />
    </Routes>
  );
};

// Component to load business information on app startup
const BusinessInfoLoader = ({ children }) => {
  useBusinessInfo(); // This will load business info and cache it
  useBranchFeatures(); // which optional features (e.g. damages) this branch has
  return children;
};

function App() {

  return (
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
          <NavigationProvider>
            <DiscountApprovalProvider>
              <BusinessInfoLoader>
                <Router>
                  <div className="App">
                    <DynamicFavicon />
                    <AppRoutes />
                    <NotificationContainer />
                    <UpdateNotifier />
                  </div>
                </Router>
              </BusinessInfoLoader>
            </DiscountApprovalProvider>
          </NavigationProvider>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
}

export default App;
